import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dafbsnbep053ijub1ng0";

type Status = "idle" | "sending" | "success";

export default function SubscribeBox() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("contact_alt") ?? "").trim();

    // 蜜罐命中：不真正提交，展示与正常一致的反馈
    if (honeypot !== "") {
      setStatus("success");
      form.reset();
      setEmail("");
      return;
    }

    const value = String(formData.get("email") ?? "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!emailOk) {
      setError("请填写有效的邮箱地址，以便接收最新瓜讯。");
      return;
    }

    const payload = new URLSearchParams();
    payload.set("email", value);

    setStatus("sending");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const okCode = parsed?.code === "OK";
      if (res.ok && okCode) {
        setStatus("success");
        form.reset();
        setEmail("");
      } else {
        setError("订阅暂未成功，请稍后再试，或检查邮箱是否正确。");
        setStatus("idle");
      }
    } catch {
      setError("网络开小差了，请稍后重试。");
      setStatus("idle");
    }
  };

  return (
    <section className="relative overflow-hidden rounded-3xl bg-foreground-950 px-6 py-10 md:px-12 md:py-14" aria-labelledby="sub-title">
      {/* 装饰光斑 */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary-500/30 blur-3xl" aria-hidden="true"></div>
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-accent-500/25 blur-3xl" aria-hidden="true"></div>

      <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-mail-send-line text-sm" aria-hidden="true"></i> 快讯订阅 · 免下载直接看
          </span>
          <h2 id="sub-title" className="mt-4 font-heading text-2xl font-black leading-snug text-background-50 md:text-3xl">
            订阅瓜讯，第一时间上桌吃瓜
          </h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-200">
            每日为你精选娱乐八卦、综艺现场与全网热讯，网页在线看、实时刷新。手机版电脑版都能直接打开，无需注册与下载。
          </p>
        </div>

        <div className="rounded-2xl bg-background-50 p-5 md:p-6">
          {status === "success" ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-check-line text-2xl" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">订阅成功</h3>
              <p className="mt-2 text-sm text-foreground-600">最新瓜讯将陆续送达你的邮箱，欢迎常来围观。</p>
            </div>
          ) : (
            <form id="subscribeForm" data-readdy-form onSubmit={handleSubmit} noValidate>
              <label htmlFor="sub-email" className="text-sm font-medium text-foreground-800">
                你的常用邮箱
              </label>
              <input
                id="sub-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="mt-2 w-full rounded-xl border border-background-300 bg-background-100/60 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50"
              />
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-honeypot"
              />
              {error && <p className="mt-2 text-sm text-primary-600">{error}</p>}
              <button
                type="submit"
                disabled={status === "sending"}
                className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                {status === "sending" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-base" aria-hidden="true"></i> 立即订阅
                  </>
                )}
              </button>
              <p className="mt-3 text-center text-xs text-foreground-500">无需注册，放心订阅，可随时退订。</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}